/**
 * Token storage for the admin panel.
 *
 * Previously both the access token and the refresh token lived in
 * `localStorage`, so any successful XSS could read a long-lived admin
 * credential synchronously and keep using it long after the page was closed.
 *
 * Interim posture (full fix is backend-issued HttpOnly cookies, which is a
 * separate piece of work):
 *
 *   - the ACCESS token is held in memory only. It never touches localStorage,
 *     so it is not readable across tabs, does not survive a reload, and an
 *     injected script has to exfiltrate it live rather than scrape it at leisure.
 *   - the REFRESH token stays in localStorage because the session has to survive
 *     a page reload and there is no cookie-based path yet. This is the remaining
 *     exposure, and it is why the CSP in public/index.html matters.
 *
 * On boot the app exchanges the refresh token for a fresh access token — see
 * AuthContext.
 */

const REFRESH_TOKEN_KEY = 'refreshToken';
// Pre-existing key; read once on startup so old sessions are cleaned up rather
// than left behind in storage.
const LEGACY_ACCESS_TOKEN_KEY = 'authToken';
// Not a credential: the username is needed to rebuild a CognitoUser after a
// reload so the refresh token can be exchanged. See AuthService.refreshSession.
const SIGNED_IN_USERNAME_KEY = 'signedInUsername';

let accessToken: string | null = null;

export const getAccessToken = (): string | null => accessToken;

export const setAccessToken = (token: string | null): void => {
  accessToken = token;
};

export const getRefreshToken = (): string | null => {
  try {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  } catch {
    return null;
  }
};

export const setRefreshToken = (token: string | null): void => {
  try {
    if (token) {
      localStorage.setItem(REFRESH_TOKEN_KEY, token);
    } else {
      localStorage.removeItem(REFRESH_TOKEN_KEY);
    }
  } catch {
    // Storage can throw in private mode / when blocked. The in-memory access
    // token still works for this page load.
  }
};

export const getSignedInUsername = (): string | null => {
  try {
    return localStorage.getItem(SIGNED_IN_USERNAME_KEY);
  } catch {
    return null;
  }
};

export const setSignedInUsername = (username: string | null): void => {
  try {
    if (username) {
      localStorage.setItem(SIGNED_IN_USERNAME_KEY, username);
    } else {
      localStorage.removeItem(SIGNED_IN_USERNAME_KEY);
    }
  } catch {
    // Non-fatal: the session simply will not survive a reload.
  }
};

/**
 * Removes credentials persisted by older builds of the panel: our own
 * `authToken` key, and the `CognitoIdentityServiceProvider.*` entries the
 * Cognito SDK wrote before it was switched to in-memory storage.
 */
export const purgeLegacyAccessToken = (): void => {
  try {
    localStorage.removeItem(LEGACY_ACCESS_TOKEN_KEY);

    for (let i = localStorage.length - 1; i >= 0; i -= 1) {
      const key = localStorage.key(i);
      if (key?.startsWith('CognitoIdentityServiceProvider.')) {
        localStorage.removeItem(key);
      }
    }
  } catch {
    // Nothing to do.
  }
};

export const clearTokens = (): void => {
  accessToken = null;
  setRefreshToken(null);
  setSignedInUsername(null);
  purgeLegacyAccessToken();
};
